<template>
  <div>
    <Filters
      :filters="stabilityFilters"
      filter-key="stabilityConfigurations"
      :initial-data="filtersStatus.stabilityConfigurations"
      :loading="filtersLoading"
    />

    <TableWrapper
      :table-columns="stabilityColumns"
      table-key="stabilityConfigurations"
      @configure="goToConfigure"
      @show="goToConfigure"
      @retry="retryExplosion"
    />

    <ViewActionButtons :buttons="actionButtons" />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import axios from 'axios';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import Filters from '@/components/Filters.vue';
  import {
    stabilityConfigurationsColumns,
    stabilityConfigurationsFilters,
  } from '@/config';
  import { stabilityApi } from '@/api';
  import tableHelper from '@/services/tableHelper';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';

  export default {
    name: 'StabilityList',
    components: {
      TableWrapper,
      Filters,
      ViewActionButtons,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        stabilityColumns: stabilityConfigurationsColumns,
        stabilityFilters: stabilityConfigurationsFilters,
        actionButtons: [],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;

        const where = tableHelper.whereFilters(
          this.filtersStatus.stabilityConfigurations,
          this.stabilityFilters
        );

        // Include relations for the list
        const tableFilters = {
          ...this.tableFilters('stabilityConfigurations'),
          include: [
            {
              relation: 'sample',
              scope: {
                include: [
                  { relation: 'order', scope: { include: ['customer'] } },
                ],
              },
            },
            { relation: 'configuredByUser' },
            { relation: 'stabilityTimePoints' },
          ],
        };

        try {
          const response = await stabilityApi.index(tableFilters, where, {
            signal,
          });
          const countResponse = await stabilityApi.count(where, { signal });

          this.updateTableRows({
            tableKey: 'stabilityConfigurations',
            rows: response.data,
          });
          this.updateTableCount({
            tableKey: 'stabilityConfigurations',
            count: countResponse.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message ||
              this.$t('stability.errors.listLoadFailed'),
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      goToConfigure(row) {
        this.$router.push({
          name: 'stabilityConfigurationsConfigure',
          params: { id: row.id },
        });
      },
      async retryExplosion(row) {
        try {
          await stabilityApi.retry(row.id);
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('stability.retryQueued'),
          });
          this.tableRows();
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message ||
              this.$t('stability.errors.retryFailed'),
          });
        }
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>
